"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 md:p-12 border border-border bg-card">
        <div className="font-serif text-2xl md:text-3xl text-foreground mb-3" style={{
        letterSpacing: "-0.015em",
        lineHeight: 1.08
      }}>
          {"Request Received."}
        </div>
        <div className="h-px w-full bg-border mb-5" />
        <p className="font-default text-base text-muted-foreground mb-8" style={{
        lineHeight: 1.65
      }}>
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="h-[52px] border-[1px] border-primary bg-transparent text-primary hover:bg-primary hover:text-white transition-colors duration-200 font-default font-semibold px-7" style={{
        letterSpacing: "0.01em"
      }}>
          {"Submit another inquiry"}
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] font-bold text-muted-foreground uppercase" style={{
        letterSpacing: "0.15em"
      }}>
              {"Full Name"}
            </FormLabel>
            <FormControl>
              <Input placeholder="John Smith" className="h-[52px] border border-border bg-background text-foreground focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 hover:border-primary transition-colors duration-200 font-default" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Work email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] font-bold text-muted-foreground uppercase" style={{
        letterSpacing: "0.15em"
      }}>
              {"Email Address"}
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john.smith@company.com" className="h-[52px] border border-border bg-background text-foreground focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 hover:border-primary transition-colors duration-200 font-default" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company_name" rules={{
      required: "Company is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] font-bold text-muted-foreground uppercase" style={{
        letterSpacing: "0.15em"
      }}>
              {"Company"}
            </FormLabel>
            <FormControl>
              <Input placeholder="Industrial Corp" className="h-[52px] border border-border bg-background text-foreground focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 hover:border-primary transition-colors duration-200 font-default" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="job_title" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] font-bold text-muted-foreground uppercase" style={{
        letterSpacing: "0.15em"
      }}>
              {"Job Title"}
            </FormLabel>
            <FormControl>
              <Input placeholder="Risk Manager" className="h-[52px] border border-border bg-background text-foreground focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 hover:border-primary transition-colors duration-200 font-default" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] font-bold text-muted-foreground uppercase" style={{
        letterSpacing: "0.15em"
      }}>
              {"Phone Number"}
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" className="h-[52px] border border-border bg-background text-foreground focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 hover:border-primary transition-colors duration-200 font-default" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] font-bold text-muted-foreground uppercase" style={{
        letterSpacing: "0.15em"
      }}>
              {"Preferred Consultation Date"}
            </FormLabel>
            <FormControl>
              <Input type="date" className="h-[52px] border border-border bg-background text-foreground focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 hover:border-primary transition-colors duration-200 font-default" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[0.75rem] font-bold text-muted-foreground uppercase" style={{
        letterSpacing: "0.15em"
      }}>
              {"Project Scope"}
            </FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Describe your facility requirements or risk management objectives..." className="min-h-[120px] border border-border bg-background text-foreground focus-visible:border-primary focus-visible:ring-0 focus-visible:ring-offset-0 hover:border-primary transition-colors duration-200 font-default py-3" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/5">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <div className="pt-2 flex flex-col sm:flex-row sm:justify-end">
        <Button type="submit" disabled={isSubmitting} className="h-[52px] w-full sm:w-auto bg-primary text-white hover:bg-accent-foreground font-default font-bold px-7" style={{
        letterSpacing: "0.01em"
      }}>
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              {"Processing..."}
            </> : "Request Consultation"}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground font-default pt-2" style={{
      lineHeight: 1.5
    }}>
        {"By submitting, you acknowledge our data handling practices per our "}
        <span className="underline decoration-border underline-offset-4 hover:decoration-primary transition-colors duration-200">
          {"privacy policy"}
        </span>
        {"."}
      </p>
    </Form>;
}
export default function Consultation() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const bullets = ["Initial 30-minute technical discovery", "Facility risk assessment overview", "Customized engineering proposal within 5 days"];
  return <section data-section-id="20222" id="secure-your-assets" className="bg-background py-24 md:py-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-[1280px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className="lg:col-span-5" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 520ms cubic-bezier(0.16, 1, 0.3, 1), transform 520ms cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
            <div className="lg:sticky lg:top-32">
              <div className="font-mono text-[0.75rem] font-bold uppercase text-primary" style={{
              letterSpacing: "0.15em",
              lineHeight: 1
            }}>
                {"§ 04 Consultation"}
              </div>
              <div className="h-px w-full bg-border mt-3 mb-6" />

              <h2 className="font-serif text-foreground font-normal" style={{
              fontSize: "clamp(2.125rem, 3.6vw, 3.25rem)",
              letterSpacing: "-0.015em",
              lineHeight: 1.08
            }}>
                {"Initiate your "}
                <em className="italic font-normal relative inline-block group">
                  {"resilience"}
                  <span className="absolute left-0 right-0 -bottom-1 h-px bg-primary scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-[380ms]" />
                </em>
                {" strategy."}
              </h2>

              <p className="font-default text-muted-foreground mt-6 max-w-md" style={{
              fontSize: "clamp(1.0625rem, 1.05vw, 1.1875rem)",
              letterSpacing: "-0.005em",
              lineHeight: 1.6
            }}>Connect with our engineering team to discuss your facility's fire protection requirements . We provide systematic risk management for high-hazard industrial environments.</p>

              <ul className="mt-10 space-y-4">
                {bullets.map((item, i) => <li data-index={i} key={i} className="flex items-start gap-4">
                    <span className="inline-block bg-primary mt-[10px] shrink-0" style={{
                  width: "8px",
                  height: "8px"
                }} />
                    <span className="font-default text-base text-foreground" style={{
                  lineHeight: 1.6
                }}>
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          {}
          <div ref={rightRef} className="lg:col-span-7" style={{
          opacity: rightVisible ? 1 : 0,
          transform: rightVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 520ms cubic-bezier(0.16, 1, 0.3, 1) 80ms, transform 520ms cubic-bezier(0.16, 1, 0.3, 1) 80ms"
        }}>
            <div className="border border-border bg-card p-8 md:p-10 lg:p-12 shadow-md">
              <FormProvider formKey="secure_assets_form_contact" formId="secure_assets_form_contact" sectionName="secure-your-assets" defaultValues={{
              full_name: "",
              email: "",
              company_name: "",
              job_title: "",
              phone: "",
              preferred_date: "",
              message: ""
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              company_name: {
                required: true
              },
              phone: {
                pattern: "phone"
              }
            }} submitText="Request Consultation" successMessage="Your inquiry has been logged. A senior engineer will review your requirements and contact you within one business day." errorMessage="Submission failed. Please verify your connection and try again.">
                <ConsultationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}